import { useId, useState, type ReactNode } from 'react';
import './dica.css';

/**
 * A dica: o texto curto que explica um elemento quando o mouse ou o foco chega
 * nele (#765). Na tela de planos, explica o selo "Inativo".
 *
 * ── O alvo entra no Tab ───────────────────────────────────────────────────
 *
 * Mesmo quando é só um selo, o alvo ganha `tabIndex=0`: sem foco, quem usa
 * teclado nunca veria o texto. O texto também vai para o `aria-describedby`,
 * que o leitor de tela lê junto com o alvo, aberto ou fechado. Esc fecha.
 *
 * A dica não leva nada clicável dentro: o que precisa de clique vai para a
 * tela, e não para um balão que some quando o mouse sai.
 */
export type DicaProps = {
  /** O texto do balão, numa frase curta. */
  texto: string;
  /** O que a dica explica: um selo, um rótulo. */
  children: ReactNode;
};

export function Dica({ texto, children }: DicaProps) {
  const id = useId();
  const [aberta, definirAberta] = useState(false);
  return (
    <span
      className="dica"
      tabIndex={0}
      aria-describedby={id}
      onMouseEnter={() => definirAberta(true)}
      onMouseLeave={() => definirAberta(false)}
      onFocus={() => definirAberta(true)}
      onBlur={() => definirAberta(false)}
      onKeyDown={(evento) => {
        if (evento.key === 'Escape') definirAberta(false);
      }}
    >
      {children}
      <span
        id={id}
        role="tooltip"
        className={aberta ? 'dica__balao dica__balao--aberta' : 'dica__balao'}
      >
        {texto}
      </span>
    </span>
  );
}
